/* eslint-disable @typescript-eslint/no-explicit-any */
import { useForm } from 'react-hook-form';
import { useContext, useRef, useState } from 'react';
import * as z from 'zod';
import { SignupSchema } from "@/utils/FormUtils/FormSchema";
import { zodResolver } from '@hookform/resolvers/zod';
import { confirmSignUp, resendCode } from '../AuthUtils/Signup';
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { CardWithNoFooter } from "@/components/ui/CardWithNoFooter";
import { AuthCard } from '@/components/Auth/AuthCard';
import { useQueryClient } from '@tanstack/react-query';
import { usePostSignup,  useSignUp } from '@/utils/Auth/Session';
import { LinearProgress } from '@mui/material';
import { useNavigate } from '@tanstack/react-router';
import { Toaster, toast } from "sonner"
import { ConfirmationStageContext } from '@/store/confirmationStage';
import { Permission } from '../../../../openapi/api/model/permission';
import { HrUser } from '../../../../openapi/api/model/hrUser';
import { AuthenticateUserResp } from '../AuthUtils/Login';
import {Icon} from 'react-icons-kit';
import {eyeOff} from 'react-icons-kit/feather/eyeOff';
import {eye} from 'react-icons-kit/feather/eye'
 

export type AddClientRedirect={
  permission?:Permission
}
export const SignUp = (redirect?:AddClientRedirect) => {
  const [pwdState, setPwdState] = useState<{
    password: { type: string; icon: JSX.Element };
    confirmPassword: { type: string; icon: JSX.Element };
  }>({
    password: { type: 'password', icon: eyeOff },
    confirmPassword: { type: 'password', icon: eyeOff }
  });

  const email=redirect?.permission?.email
  const {setConfirmationStage} = useContext(ConfirmationStageContext)
  const { register, handleSubmit, formState: {errors}, getValues } = useForm<z.infer<typeof SignupSchema>>({
    resolver: zodResolver(SignupSchema),
    defaultValues: {
      email: email??"",
      password: "",
      confirmPassword: "",
    }});
  const navigate=useNavigate({from:'/'})
  const [signupCodeStatus,setSignupCodeStatus]=useState<'idle' | 'in progress'>('idle')
  const queryClient=useQueryClient()
  const permission=redirect?.permission
  const resp=queryClient.getQueryData(['Auth']) as AuthenticateUserResp
  const sub=resp?.session?.getIdToken()?.payload.sub as string
  const hrUser:HrUser={
      email:permission?.email??'',
      licenseId:permission?.licenseId??'',
      sub:sub,
      userName:getValues('userName'),
      //add additional fields here
      firstName:getValues('firstName'),
      lastName:getValues('lastName'),
      position:'Hiring Manager',
      company:getValues('company'),
      phone:'+254712345678'
  }


   
  
  const {postSignupRoutine,errors:createAccountErrs,loading:postSignupProgress}=usePostSignup({
    queryClient,
    onSuccess:()=>{}
  })
   
  const {mutateAsync:SignUp,isSuccess:signupSuccess,isPending:signUpLoading}=useSignUp({
    onFailureFunc:()=>{}
      })
  if(createAccountErrs){
    console.log({createAccountErrs})
  }

  

  const submitRef = useRef(null) as any
  const _triggerSubmit = () => {
    submitRef?.current?.click(); // Programmatically click the hidden submit button
  };
  const { register:confirmationCodeRegister, handleSubmit:confirmSubmit  } = useForm()

  const onSubmit = async (data:any) => {
    try{
      await SignUp({
        name:data.userName,
        email:data.email,
        password:data.password,
        phone_number:'+254712345678',
      })
    }catch(err){
      toast('Failed to sign up', {
        description:(err as Error).message,
        action: {
          label: "Dismiss",
          onClick: () => console.log("Dismiss"),
        },
      });
      return
    
    }

  };



  const resendConfirmationCode=()=>{
    const userName=getValues('userName')
    try{
      resendCode({userName:userName})
    }catch(e){
      toast('Try again later', {
        description: (e as Error).message,
        action: {
          label: "Dismiss",
          onClick: () => console.log("Dismiss"),
        },
      });
    }
    
  }


  const confirmEmail = async (data:any) => {
    const userName=getValues('userName');
    const password=getValues('password');
    setSignupCodeStatus('in progress')
    try{
      await confirmSignUp({
          code:data.confirmationCode,
          userName
        })
      setSignupCodeStatus('idle')
      //post singup routine for the redirect workflow (invite link)
      if(!(redirect && redirect.permission)) return
      await postSignupRoutine({hrUser,password})
      navigate({to:'/profile'})
    }catch(e){
      toast('Failed to confirm email', {
        description:(e as Error).message,
        action: {
          label: "Dismiss",
          onClick: () => console.log("Dismiss"),
        },
      });
      return
    }
  }


  const handlePasswordVisibility = (field: 'password' | 'confirmPassword') => {
    setPwdState(prevState => ({
      ...prevState,
      [field]: {
        type: prevState[field].type === 'password' ? 'text' : 'password',
        icon: prevState[field].type === 'password' ? eye : eyeOff
      }
    }));
  };


  if(signupSuccess){
    setConfirmationStage(true)
    return (
      <>
      <CardWithNoFooter 
      cardTitle="Confirm e-mail"
      cardDescription="Enter the confirmation code sent to your email">
        <form onSubmit={confirmSubmit(confirmEmail)} className="flex flex-col space-y-1.5">
          <Input 
            id={'confirmEmail'} 
            type={'text'} 
            {...confirmationCodeRegister('confirmationCode')} 
            name={'confirmationCode'}
            className='border-primary100 border-2 rounded-xl text-center'/>
          <div className="min-h-40">
          {signupCodeStatus==='in progress' && <LinearProgress/> }
          </div>
          <Button type='submit' className="bg-primary200 rounded-full">Confirm</Button>
        </form>
        <Button
        variant={'link'}
        onClick={resendConfirmationCode}>
          Resend code
        </Button>
      </CardWithNoFooter>    
      <Toaster/>   
      </>

    )
  }


  return (
    <AuthCard 
    loading={signUpLoading || postSignupProgress}
    cardTitle="Let's create your account"
    buttonText='Continue'
    //cardDescription="Enter your email and password to create an account"
    onSubmit={_triggerSubmit}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-row space-x-2 py-3">
          <Input 
            id="firstName" 
            type="text" 
            {...register("firstName")} 
            name="firstName"
            placeholder="First Name"
            className='border-primary100 border-2 rounded-xl text-center' />
          <Input 
            id="lastName" 
            type="text" 
            {...register("lastName")} 
            name="lastName"
            placeholder="Last Name"
            className='border-primary100 border-2 rounded-xl text-center' />
        </div>
        <div className="grid w-full items-center gap-3">
          <div className="flex flex-col space-y-1.5" key="userName">
            <Input 
              id="userName" 
              type="text" 
              {...register("userName")} 
              name="userName"
              placeholder="Username"
              className="border-primary100 border-2 rounded-xl text-center"/>
            {errors["userName"] && errors["userName"].message && <p className="text-red-500 text-sm">{errors["userName"].message}</p>}
          </div>
          <div className="flex flex-col space-y-1.5" key="userName">
            <Input 
              id="company" 
              type="text" 
              {...register("company")} 
              name="company"
              placeholder="Company"
              className="border-primary100 border-2 rounded-xl text-center"/>
            {errors["company"] && errors["company"].message && <p className="text-red-500 text-sm">{errors["company"].message}</p>}
          </div>
          <div className="flex flex-col space-y-1.5" key="email">
            <Input 
              id="email" 
              type="email" 
              {...register("email")} 
              name="email"
              placeholder="Email"
              readOnly={!!email}
              className="border-primary100 border-2 rounded-xl text-center"/>
            {errors["email"] && errors["email"].message && <p className="text-red-500 text-sm">{errors["email"].message}</p>}
          </div>
          <div className="flex flex-col space-y-1.5" key="password">
            <div className="flex">
              <Input 
                id="password" 
                type={pwdState.password.type} 
                {...register("password")} 
                name="password"
                placeholder="Password"
                className="border-primary100 border-2 rounded-xl text-center"/>
              <span className="flex justify-around items-center" onClick={() => handlePasswordVisibility('password')}>
                <Icon className="absolute mr-10" icon={pwdState.password.icon} size={15}/>
              </span>
            </div>
            {errors["password"] && errors["password"].message && <p className="text-red-500 text-sm">{errors["password"].message}</p>}
          </div>
          <div className="flex flex-col space-y-1.5" key="confirmPassword">
            <div className="flex">
              <Input 
                id="confirmPassword" 
                type={pwdState.confirmPassword.type} 
                {...register("confirmPassword")} 
                name="confirmPassword"
                placeholder="Confirm Password"
                className="border-primary100 border-2 rounded-xl text-center"/>
              <span className="flex justify-around items-center" onClick={() => handlePasswordVisibility('confirmPassword')}>
                <Icon className="absolute mr-10" icon={pwdState.confirmPassword.icon} size={15}/>
              </span>
            </div>
            {errors["confirmPassword"] && errors["confirmPassword"].message && <p className="text-red-500 text-sm">{errors["confirmPassword"].message}</p>}
          </div>
        </div>
        <p className="text-2xs text-center text-primary200 mt-2">
          By clicking Continue, you agree to the {' '} 
          <button className="underline">Terms of Service</button> and {' '}  
          <button className="underline">Privacy Policy</button>
        </p>
        <input type="submit" value="Submit" style={{ display: 'none' }} ref={submitRef} />
      </form>
    </AuthCard>
  )


}

